import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocker } from '../../../context/hardware';
import { useKioskImage, useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { CTAButton } from '../../../controls/ButtonBase';
import { GMDialogRetryCancel } from '../../../controls/Dialog';
import { Heading } from '../../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../../controls/KioskImageDisplay';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { KeyDropPickupProps } from './KeyDropPickup.types';
import { AppointmentLoanerOrRentalOption, Events } from '../../../API/Enums';
import { Locales, LocalizationType } from '../../../context/Localization/localization.types';
import { useAppointmentState, useLockerOperation } from '../../../context/Appointment/appointment';
import { KeyDropError } from '../../../context/Appointment/appointment.types';
import { IAppointment, insertAuditTrail } from '../../../API/GoMoto';

/**
 * This component handles the process of returning a customer's key during a pick-up or exchanging
 * a customer's key with a rental or loaner key. When the customer has scanned a QR Code, the locker associated
 * with their QR Code will open and the screen will display the instructions they need to follow to complete
 * their appointment and pick up their vehicle.
 *
 * @param onSkip - {@link KeyDropPickupProps.onSkip onSkip} description.
 * @param onProceed - {@link KeyDropPickupProps.onProceed onProceed} description.
 */
export const KeyDropPickUp: GoMotoComponent<KeyDropPickupProps> = ({
    onSkip,
    onProceed,
}) => {
    useHideKioskBackground()();
    const Localization: LocalizationType = useLocalization();
    const appointment: IAppointment = useAppointmentState();
    const keyPickUpImg: string = useKioskImage('KeyPickUp');
    const kioskImage: string = useKioskImage('RetrieveKey');
    const {
        start,
        inProgress,
        complete,
        hasOpened,
        error: keyRetrieveError,
        forceLockerClose,
    } = useLockerOperation();
    const {
        lockersEnabled,
        error: lockerError,
    } = useLocker();
    const {
        kioskType,
        keyStorageType,
    } = useKioskState();

    const locale = useLocale();
    const [keySlotNumber, setKeySlotNumber] = useState<number>(appointment.keySlotNumber ?? 0);
    const [error, setError] = useState<string | undefined>(lockerError);
    const mounted = useRef<boolean>(false);
    const isKeyExchange: boolean = !!appointment.returningLoanerOrRental && appointment.returningLoanerOrRental !== AppointmentLoanerOrRentalOption.None;

    const startOpenLocker: GoMotoFunction<[void], Promise<void>> = useCallback(async () => {
        if (appointment.keyStatus === 'reopen requested pickup' || appointment.keyStatus === 'reopen requested exchange') {
            insertAuditTrail(appointment.key, Events.ReopenedLocker, undefined, appointment.keySlotNumber?.toString());
            start({
                lockerAction: 'open-locker',
                completeAppointment: true,
            });
        }
        else if (isKeyExchange) {
            start({
                lockerAction: 'pickup-and-exchange-key',
            });
        }
        else {
            await start({
                lockerAction: 'retrieve-key',
                completeAppointment: true,
            })
                .catch(setError);
        }
    }, [appointment.key, appointment.keyStatus, isKeyExchange, start, appointment.keySlotNumber]);

    const lockerClosed: VoidFunction = useCallback(async () => {
        if (!complete) {
            await forceLockerClose();
        }
        onProceed();
    }, [complete, forceLockerClose, onProceed]);

    useEffect(() => {
        if (!lockersEnabled) {
            onSkip();
        }
    }, [lockersEnabled, onSkip]);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;

            startOpenLocker();
        }
    }, [startOpenLocker]);

    useEffect(() => {
        if (!keySlotNumber) {
            setKeySlotNumber(keySlotNumber);
        }
    }, [appointment.keySlotNumber, keySlotNumber]);

    useEffect(() => {
        if (keyRetrieveError !== KeyDropError.none) {
            setError(Localization.FailedToOpenLocker);
        }
    }, [keyRetrieveError, Localization.FailedToOpenLocker]);

    useEffect(() => {
        if (keyStorageType === 'locker' && !inProgress && complete) {
            onProceed();
        }
    }, [keyStorageType, inProgress, complete, onProceed]);

    if (!lockersEnabled) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={isKeyExchange ? Localization.ExchangeKeysNow : Localization.GetKeyNow}
                        secondaryHeading={kioskType === 'express' ? (
                            isKeyExchange ? (
                                Localization.formatString(
                                    Localization.PleaseRetrieveYourKeyFromLockerAndReturnKey,
                                    <>
                                        {appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                            Localization.Loaner.toLowerCase()
                                        ) : (
                                            Localization.Rental.toLowerCase()
                                        )}
                                    </>,
                                    <b>{Localization.formatString(Localization.LockerNum, keySlotNumber)}</b>,
                                ) as string
                            ) : (
                                Localization.formatString(
                                    Localization.PleaseRetrieveYourKeyFromLocker,
                                    <b>{Localization.formatString(Localization.LockerNum, keySlotNumber)}</b>,
                                )
                            )
                        ) : (
                            isKeyExchange ? (
                                Localization.formatString(
                                    Localization.PleaseRetrieveYourKeyAndReturnKey,
                                    appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                        Localization.Loaner.toLowerCase()
                                    ) : (
                                        Localization.Rental.toLowerCase()
                                    ),
                                )
                            ) : (
                                Localization.PleaseRetrieveYourKey
                            )
                        )}
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon iconType={'Key'} iconSize={'large'} />
                            </GMRoundedIcon>
                        }
                    />
                }
                proceedButtons={
                    keyStorageType === 'locker' && inProgress && !error ? (
                        <GMButtonGroup
                            buttons={[
                                <CTAButton
                                    onClick={lockerClosed}
                                    styleType={'primary'}
                                    label={Localization.LockerClosed}
                                    kioskOptions={{ inlineOptions: { sizedIcon: <GMSizedIcon iconType={'Complete'} ariaLabel={Localization.CompleteCheckmarkIcon} /> } }}
                                    id={Localization.LockerClosed}
                                    ariaLabel={Localization.LockerClosed}
                                    disabled={!hasOpened}
                                />,
                            ]}
                        />
                    ) : (!inProgress && complete && !error && keyStorageType !== 'locker' ? (
                        <GMButtonGroup
                            buttons={[
                                <CTAButton
                                    onClick={lockerClosed}
                                    styleType={'primary'}
                                    label={Localization.KeyReceived}
                                    kioskOptions={{ inlineOptions: { sizedIcon: <GMSizedIcon iconType={'Complete'} ariaLabel={Localization.CompleteCheckmarkIcon} /> } }}
                                    id={Localization.KeyReceived}
                                    ariaLabel={Localization.KeyReceived}
                                />,
                            ]}
                        />
                    ) : undefined)
                }
            >
                <KioskImageDisplay
                    left={{
                        image: keyPickUpImg,
                    }}
                    right={{
                        header: {
                            text: isKeyExchange ? (
                                Localization.formatString(
                                    Localization.CarReturnHeader,
                                    appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                        locale === Locales.fr ? Localization.Loaner.toLocaleLowerCase() : Localization.Loaner
                                    ) : (
                                        locale === Locales.fr ? Localization.Rental.toLocaleLowerCase() : Localization.Rental
                                    ),
                                )
                            ) as string : undefined,
                            isAlertText: isKeyExchange,
                        },
                        top: {
                            text: isKeyExchange ? (
                                Localization.formatString(
                                    Localization.ReturnTheKeyToTheOpenLocker,
                                    appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                        Localization.Loaner.toLowerCase()
                                    ) : (
                                        Localization.Rental.toLowerCase()
                                    ),
                                ) as string
                            ) : (
                                kioskType === 'express' ? (
                                    Localization.RetrieveYourKeyFromOpenLocker
                                ) : (
                                    Localization.RetrieveYourKey
                                )
                            ),
                            isAlertText: isKeyExchange,
                        },
                        image: kioskImage,
                        bottom: Localization.ThankYouForServicingYourVehicle,
                    }}
                />

                <GMDialogRetryCancel
                    ariaLabel={Localization.Retry}
                    open={!!error}
                    onCancel={() => {
                        onSkip();
                    }}
                    onRetry={() => {
                        setError('');
                        startOpenLocker();
                    }}
                >
                    {error}
                </GMDialogRetryCancel>
            </ScrollableBody>
        );
    }
};
